/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Button, Icon, Popup } from '@mairistem/composants';
import { request } from '@mairistem/tools';
import {
  HistoryModal,
  type TypeActeAnnotable,
  urlDocumentFusion,
  useLayers,
} from '@mairistem/administres-utils';

import COLORS from '@/constants/colors';
import useApercuDocument from '@/hooks/useApercuDocument';
import useDocumentsActe from '@/hooks/useDocumentsActe';
import type { TypeActe } from '@/types';

import ActeDocumentsListe from './ActeDocumentsListe';
import { ActeDocumentAnnotation, ConfirmationModal } from './Annotation';

type Onglet = 'liste' | 'annotation';

/**
 * Onglets en boutons accolés, comme la maquette : l'actif en bleu plein, l'autre
 * en gris. `Menu secondary` de Semantic donnait des onglets soulignés, assez
 * loin du rendu attendu.
 */
const styleOnglet = (actif: boolean): React.CSSProperties => ({
  backgroundColor: actif ? COLORS.Background : COLORS.LightGreyCardColor,
  color: actif ? 'white' : COLORS.GreyTextColor,
  fontWeight: actif ? 600 : 400,
  minWidth: '190px',
});

/**
 * Hauteur utile de l'écran, mesurée sous la barre de la coquille 360.
 *
 * Aucun parent ne porte de hauteur définie : les `height: 100%` des panneaux se
 * résolvaient donc en `auto`, et la liste des documents s'allongeait
 * indéfiniment — la page entière défilait au lieu de la seule liste.
 *
 * L'écart au haut du document est stable une fois l'écran contenu dans la
 * fenêtre, puisque la page ne défile alors plus.
 */
const useHauteurDisponible = () => {
  const conteneur = React.useRef<HTMLDivElement>(null);
  const [hauteur, setHauteur] = React.useState<string>();

  React.useLayoutEffect(() => {
    const mesurer = () => {
      const noeud = conteneur.current;
      if (!noeud) return;

      const haut = noeud.getBoundingClientRect().top + window.scrollY;
      setHauteur(`calc(100vh - ${Math.round(haut)}px)`);
    };

    mesurer();
    window.addEventListener('resize', mesurer);

    return () => window.removeEventListener('resize', mesurer);
  }, []);

  return { conteneur, hauteur };
};

interface ActeDocumentsPageProps {
  /** Acte dont on gère les documents. */
  identifiantActe?: number;
  typeActe: TypeActe;
  /** Titre affiché sous la barre, ex. « Acte de naissance de … ». */
  titre?: string;
  /** Chemin de retour vers la fiche. */
  retour?: string;
}

/**
 * Écran des documents d'un acte : liste et annotation, comme les deux onglets
 * de la maquette.
 *
 * La page porte la **barre d'actions**, sur le modèle de la `FicheBar` d'un
 * acte : retour, enregistrement et annulation des annotations, onglets,
 * historique. C'est pourquoi elle charge elle-même les calques et les passe à
 * l'onglet d'annotation plutôt que de le laisser les charger — les deux ont
 * besoin du même état.
 *
 * L'annotation porte sur le document sélectionné dans la liste : les calques
 * sont rattachés au document, pas à l'acte.
 */
const ActeDocumentsPage = (props: ActeDocumentsPageProps) => {
  const { identifiantActe, typeActe, titre, retour } = props;

  const history = useHistory();

  const [onglet, setOnglet] = React.useState<Onglet>('liste');
  const [selection, setSelection] = React.useState<number | undefined>();
  const [pageCourante, setPageCourante] = React.useState(1);
  const [historiqueOuvert, setHistoriqueOuvert] = React.useState(false);
  const [telechargement, setTelechargement] = React.useState(false);
  /** Action à rejouer si l'utilisateur accepte d'abandonner ses modifications. */
  const [sortieEnAttente, setSortieEnAttente] = React.useState<
    (() => void) | undefined
  >();

  /*
   * Les documents, l'aperçu et les calques appartiennent à l'écran, pas à ses
   * onglets : sinon chaque bascule démonterait l'onglet courant et
   * retéléchargerait la liste, le binaire et les calques du même document.
   */
  const { documents, loading, ajouter, modifier, refresh } = useDocumentsActe(
    identifiantActe,
    typeActe,
  );

  const documentSelectionne = React.useMemo(
    () => _.find(documents, { identifiant: selection }),
    [documents, selection],
  );

  // Sélectionne le premier document, et rattrape la sélection quand le
  // document affiché vient d'être supprimé.
  React.useEffect(() => {
    if (_.isEmpty(documents)) {
      setSelection(undefined);
      return;
    }
    if (!_.some(documents, { identifiant: selection })) {
      setSelection(documents[0].identifiant);
    }
  }, [documents, selection]);

  const { pages, format, binaire, chargement, sansContenu } =
    useApercuDocument(documentSelectionne);

  // Repartir de la première page à chaque changement de document. `selection`
  // n'est pas lue dans l'effet, mais c'est bien son changement qui doit le
  // déclencher.
  // biome-ignore lint/correctness/useExhaustiveDependencies: cf. ci-dessus
  React.useEffect(() => {
    setPageCourante(1);
  }, [selection]);

  /*
    Seul le TIFF est annotable : la fusion des calques passe par `TiffMerger`
    et leur import par `WangReader`, tous deux propres à ce format.

    Le critère est le format **décodé**, pas `dc_type` : celui-ci est vide sur
    des milliers de documents, et l'admettre ouvrait l'annotation aux
    formulaires RTF, qui sont justement les documents sans type.
  */
  const annotable = !_.isNil(documentSelectionne) && format === 'tiff';

  // Les routes de calques et de mentions n'acceptent que quatre types d'acte ;
  // `TypeActe` en déclare davantage.
  const typeActeAnnotable = React.useMemo(
    () =>
      _.includes(['naissance', 'reconnaissance', 'mariage', 'deces'], typeActe)
        ? (typeActe as TypeActeAnnotable)
        : undefined,
    [typeActe],
  );

  /*
    Les calques ne sont chargés que pour un document annotable. Ce n'est pas
    qu'une économie de requête : `GET /documents/{id}/calques` déclenche côté
    API le parsing des balises WANG de la pièce et bascule `dc_tiff_parsed`,
    traitement qui n'a aucun sens sur un JPEG ou un PDF.

    Le format n'étant connu qu'après décodage, la requête part une fois le
    document lu — soit au moment où l'onglet d'annotation devient accessible.
  */
  const layers = useLayers(
    annotable ? (documentSelectionne?.identifiant ?? 0) : 0,
    typeActeAnnotable,
    'document',
  );

  // Un changement de document peut rendre l'onglet courant inaccessible.
  React.useEffect(() => {
    if (!annotable) setOnglet('liste');
  }, [annotable]);

  /**
   * Protège les sorties de l'onglet d'annotation : quitter avec des calques non
   * enregistrés les perdrait sans avertissement.
   */
  const quitterAnnotation = React.useCallback(
    (action: () => void) => {
      if (onglet === 'annotation' && layers.updated) {
        setSortieEnAttente(() => action);
        return;
      }
      action();
    },
    [onglet, layers.updated],
  );

  const handleRetour = React.useCallback(() => {
    if (!retour) return;
    quitterAnnotation(() => history.push(retour));
  }, [history, retour, quitterAnnotation]);

  const handleOngletListe = React.useCallback(() => {
    quitterAnnotation(() => setOnglet('liste'));
  }, [quitterAnnotation]);

  const handleAbandonner = React.useCallback(() => {
    layers.refresh();
    sortieEnAttente?.();
    setSortieEnAttente(undefined);
  }, [layers.refresh, sortieEnAttente]);

  const handleEnregistrerPuisSortir = React.useCallback(() => {
    layers.save();
    sortieEnAttente?.();
    setSortieEnAttente(undefined);
  }, [layers.save, sortieEnAttente]);

  /**
   * Télécharge le document annoté, calques fusionnés côté serveur.
   *
   * Repris de GAN, où le bouton n'apparaissait qu'en dehors de WinDev : l'hôte
   * WinDev n'ouvrait pas les téléchargements du navigateur. Ici on est toujours
   * dans le navigateur, le bouton est donc permanent.
   *
   * L'enregistrement précède le téléchargement — c'est aussi ce que faisait
   * GAN : la fusion se fait à partir des calques **stockés**, des annotations
   * non enregistrées manqueraient au rendu.
   */
  const handleTelechargement = React.useCallback(() => {
    const doc = documentSelectionne;
    if (!doc) return;

    setTelechargement(true);

    const echec = () => {
      toast('Erreur lors du téléchargement du document', { type: 'error' });
      setTelechargement(false);
    };

    layers
      .save()
      .then(() => {
        const parametres = new URLSearchParams({
          pages: String(Math.max(_.size(pages), 1)),
          // `api=0` demande le rendu fusionné plutôt que le JSON de l'aperçu.
          api: '0',
        });

        request
          .get<unknown, Blob>(
            `${urlDocumentFusion(doc.identifiant)}?${parametres.toString()}`,
            { headers: { Accept: 'image' }, responseType: 'blob' },
          )
          .then((contenu: Blob) => {
            /*
              `request.download` n'est pas utilisable : la passerelle ne
              retransmet pas l'en-tête `Content-Disposition`, constat déjà fait
              dans GAN. On déclenche donc le téléchargement à la main.
            */
            const url = URL.createObjectURL(contenu);
            const lien = window.document.createElement('a');
            lien.href = url;
            // L'API rend un PDF dès lors que `api=0`, quel que soit le nombre
            // de pages.
            lien.download = `${doc.nom || 'document'}.pdf`;
            lien.click();

            // Laisser au navigateur le temps de démarrer le téléchargement.
            setTimeout(() => {
              URL.revokeObjectURL(url);
              lien.remove();
            }, 5000);

            setTelechargement(false);
          })
          .catch(echec);
      })
      .catch(echec);
  }, [documentSelectionne, layers.save, pages]);

  const enAnnotation = onglet === 'annotation';
  const occupe = layers.loading;

  const { conteneur, hauteur } = useHauteurDisponible();

  return (
    /*
      Même gabarit que la fiche d'un acte : barre d'actions puis ligne du titre,
      toutes deux en pleine largeur, et le contenu en dessous. Pas de `Segment`
      englobant, qui enfermerait la barre dans un cadre arrondi.
    */
    <div
      ref={conteneur}
      data-testid="documents-ecran"
      style={{
        height: hauteur ?? '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <div
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#F1FAFE',
          padding: '0.6em 1em',
        }}
      >
        {/*
          Les actions sont sorties du flux pour que les onglets restent centrés
          sur la page, quel que soit le nombre d'icônes affichées.
        */}
        <div
          style={{
            position: 'absolute',
            left: '1em',
            display: 'flex',
            alignItems: 'center',
            gap: '1.2em',
          }}
        >
          {retour && (
            <Icon
              link
              name="arrow left"
              size="large"
              data-testid="documents-retour"
              title="Retour à la fiche"
              onClick={handleRetour}
            />
          )}
          {enAnnotation && (
            <>
              <Icon
                link
                data-testid="annotation-enregistrer"
                name={occupe ? 'spinner' : 'check'}
                loading={occupe}
                size="large"
                title="Enregistrer les annotations"
                onClick={layers.save}
                style={{ visibility: layers.updated ? 'visible' : 'hidden' }}
              />
              <Icon
                link
                data-testid="annotation-annuler"
                name={occupe ? 'spinner' : 'undo'}
                loading={occupe}
                size="large"
                title="Annuler les modifications"
                onClick={() => layers.refresh()}
                style={{ visibility: layers.updated ? 'visible' : 'hidden' }}
              />
              <Icon
                link
                data-testid="annotation-telecharger"
                name={telechargement ? 'spinner' : 'download'}
                loading={telechargement}
                size="large"
                title="Enregistrer et télécharger le document annoté"
                onClick={handleTelechargement}
              />
            </>
          )}
        </div>

        <Button.Group>
          <Button
            data-testid="onglet-liste"
            content="Liste des documents"
            style={styleOnglet(onglet === 'liste')}
            onClick={handleOngletListe}
          />
          <Popup
            disabled={annotable}
            position="bottom center"
            size="small"
            content={
              // Pendant le décodage, le format n'est pas encore connu : parler
              // de TIFF serait un contresens le temps du chargement.
              !documentSelectionne
                ? 'Sélectionnez un document à annoter.'
                : chargement
                  ? 'Analyse du document en cours…'
                  : 'Seuls les documents TIFF peuvent recevoir des annotations.'
            }
            /*
             * L'onglet n'utilise pas la prop `disabled` de Semantic : elle pose
             * `pointer-events: none`, donc le survol ne l'atteindrait jamais et
             * l'infobulle ne s'afficherait pas — précisément quand elle sert.
             * On reproduit l'aspect désactivé, et le clic reste sans effet.
             */
            trigger={
              <Button
                data-testid="onglet-annotation"
                content="Annotations du document"
                aria-disabled={!annotable}
                style={{
                  ...styleOnglet(onglet === 'annotation'),
                  ...(annotable
                    ? {}
                    : { opacity: 0.55, cursor: 'not-allowed' }),
                }}
                onClick={() => annotable && setOnglet('annotation')}
              />
            }
          />
        </Button.Group>

        {enAnnotation && (
          <Icon
            link
            data-testid="annotation-historique"
            name="history"
            size="large"
            title="Historique des modifications"
            style={{ marginLeft: '1.2em' }}
            onClick={() => setHistoriqueOuvert(true)}
          />
        )}
      </div>

      {titre && (
        <div
          style={{
            backgroundColor: 'white',
            borderBottom: `1px solid ${COLORS.DividerColor}`,
            padding: '0.7em 1.5em',
            fontWeight: 'bold',
          }}
        >
          {titre}
        </div>
      )}

      <div style={{ flex: 1, minHeight: 0 }}>
        {onglet === 'liste' ? (
          <ActeDocumentsListe
            documents={documents}
            selection={selection}
            onSelectionChange={setSelection}
            pages={pages}
            format={format}
            binaire={binaire}
            sansContenu={sansContenu}
            /*
              Le chargement de la liste ne concerne l'aperçu que tant qu'aucun
              document n'est sélectionné : le rafraîchir après un renommage ne
              doit pas remplacer l'aperçu affiché par un panneau d'attente.
            */
            chargement={chargement || (loading && _.isNil(selection))}
            pageCourante={pageCourante}
            onPageChange={setPageCourante}
            onAjouter={ajouter}
            onModifier={modifier}
            onRefresh={refresh}
            acteConnu={!_.isNil(identifiantActe)}
          />
        ) : (
          <ActeDocumentAnnotation
            identifiantActe={identifiantActe}
            typeActe={typeActeAnnotable}
            layers={layers}
            pages={pages}
            isTiff={format === 'tiff'}
            chargement={chargement}
            pageCourante={pageCourante}
            onPageChange={setPageCourante}
          />
        )}
      </div>

      <ConfirmationModal
        open={!_.isNil(sortieEnAttente)}
        onClose={() => setSortieEnAttente(undefined)}
        onContinueWithoutSaveModal={handleAbandonner}
        onSaveLayersModal={handleEnregistrerPuisSortir}
      />
      <HistoryModal
        open={historiqueOuvert}
        onClose={() => setHistoriqueOuvert(false)}
        layers={layers}
        pages={pages}
      />
    </div>
  );
};

export default ActeDocumentsPage;
